import {useI18n} from '@/i18n'
import {AssignmentEventType, AssignmentSourceType} from '@/types/assignment'
import {FunctionGroupAssignmentStatus} from '@/types/function-group'
import {type IdentityLifecycleActionTarget, IdentityLifecycleState} from '@/types/identity'
import {Bot, Download, FileText, HelpCircle, KeyRound, type LucideIcon} from 'lucide-react'

// -------- i18n-backed label hooks --------
// Each hook returns a closure over the current locale's i18n proxy. Call the
// hook at the top of a component; give the returned value a descriptive local
// name; use that name as a 1-arg function wherever the enum is rendered.

export function useIdentityLifecycleActionLabel() {
    const i18n = useI18n()
    return (target: IdentityLifecycleActionTarget): string => {
        switch (target) {
            case IdentityLifecycleState.ACTIVE:
                return i18n.identities.activate
            case IdentityLifecycleState.SUSPENDED:
                return i18n.identities.suspend
            case IdentityLifecycleState.TERMINATED:
                return i18n.identities.terminate
        }
    }
}

export function useFunctionGroupAssignmentStatusLabel() {
    const i18n = useI18n()
    return (status: FunctionGroupAssignmentStatus): string => {
        switch (status) {
            case FunctionGroupAssignmentStatus.ACTIVE:
                return i18n.functionGroupAssignments.statusActive
            case FunctionGroupAssignmentStatus.PENDING:
                return i18n.functionGroupAssignments.statusPending
            case FunctionGroupAssignmentStatus.EXPIRED:
                return i18n.functionGroupAssignments.statusExpired
            case FunctionGroupAssignmentStatus.REVOKED:
                return i18n.functionGroupAssignments.statusRevoked
        }
    }
}

export function useAssignmentSourceTypeLabel() {
    const i18n = useI18n()
    return (sourceType: AssignmentSourceType): string => {
        switch (sourceType) {
            case AssignmentSourceType.RULE:
                return i18n.functionGroupAssignments.rule
            case AssignmentSourceType.REQUEST:
                return i18n.functionGroupAssignments.request
            case AssignmentSourceType.API:
                return i18n.functionGroupAssignments.api
            case AssignmentSourceType.IMPORT:
                return i18n.functionGroupAssignments.import
            default:
                return i18n.functionGroupAssignments.unknownSource
        }
    }
}

export function useAssignmentEventTypeLabel() {
    const i18n = useI18n()
    return (eventType: AssignmentEventType): string => {
        switch (eventType) {
            case AssignmentEventType.CREATED:
                return i18n.functionGroupAssignments.eventCreated
            case AssignmentEventType.EXTENDED:
                return i18n.functionGroupAssignments.eventExtended
            case AssignmentEventType.SHORTENED:
                return i18n.functionGroupAssignments.eventShortened
            case AssignmentEventType.REVOKED:
                return i18n.functionGroupAssignments.eventRevoked
            case AssignmentEventType.RESTORED:
                return i18n.functionGroupAssignments.eventRestored
            case AssignmentEventType.SOURCE_ADDED:
                return i18n.functionGroupAssignments.eventSourceAdded
            case AssignmentEventType.SOURCE_REMOVED:
                return i18n.functionGroupAssignments.eventSourceRemoved
            case AssignmentEventType.SOURCE_RESTORED:
                return i18n.functionGroupAssignments.eventSourceRestored
        }
    }
}

// -------- Pure resolvers (no i18n — variants, icons, colors) --------

export function assignmentSourceTypeIcon(sourceType: AssignmentSourceType): LucideIcon {
    switch (sourceType) {
        case AssignmentSourceType.RULE:
            return Bot
        case AssignmentSourceType.REQUEST:
            return FileText
        case AssignmentSourceType.API:
            return KeyRound
        case AssignmentSourceType.IMPORT:
            return Download
        default:
            return HelpCircle
    }
}

export function assignmentEventTypeColor(eventType: AssignmentEventType): string {
    switch (eventType) {
        case AssignmentEventType.CREATED:
        case AssignmentEventType.RESTORED:
        case AssignmentEventType.SOURCE_RESTORED:
            // eslint-disable-next-line i18next/no-literal-string -- CSS utility class
            return 'text-status-success'
        case AssignmentEventType.EXTENDED:
        case AssignmentEventType.SOURCE_ADDED:
            // eslint-disable-next-line i18next/no-literal-string -- CSS utility class
            return 'text-status-info'
        case AssignmentEventType.SHORTENED:
        case AssignmentEventType.SOURCE_REMOVED:
            // eslint-disable-next-line i18next/no-literal-string -- CSS utility class
            return 'text-status-warning'
        case AssignmentEventType.REVOKED:
            // eslint-disable-next-line i18next/no-literal-string -- CSS utility class
            return 'text-status-danger'
    }
}
